import React from 'react';
import { FaCalendarAlt, FaExclamationTriangle } from 'react-icons/fa';

interface ScheduleConflictPopupProps {
  isOpen: boolean;
  invalidItems: any[];
  onClose: () => void;
  onProceedWithoutInvalid: () => void;
}

const formatDisplayDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDisplayTime = (timeStr: string) => {
  if (!timeStr || !timeStr.includes(':')) return timeStr;
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return timeStr;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export const ScheduleConflictPopup: React.FC<ScheduleConflictPopupProps> = ({
  isOpen,
  invalidItems,
  onClose,
  onProceedWithoutInvalid,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-conflict-title"
        aria-describedby="schedule-conflict-description"
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col p-6 sm:p-8 border border-gray-100"
      >
        <div className="flex flex-col items-center text-center mb-5">
          <div className="p-3.5 bg-amber-50 text-amber-600 rounded-2xl mb-3 shadow-sm border border-amber-100">
            <FaExclamationTriangle className="w-6 h-6" />
          </div>

          <h3
            id="schedule-conflict-title"
            className="text-xl font-bold text-gray-900 leading-snug"
          >
            Schedule Update Needed
          </h3>

          <p
            id="schedule-conflict-description"
            className="text-sm text-gray-600 mt-1.5 leading-relaxed max-w-md"
          >
            Some items in your cart aren't available for your selected pickup time. Update your schedule to keep them, or remove them to proceed.
          </p>
        </div>

        <div className="flex items-center justify-between mb-2.5 px-1">
          <span className="text-xs font-bold tracking-wider text-gray-500 uppercase">
            Unavailable Items ({invalidItems.length})
          </span>
        </div>

        <div className="max-h-60 overflow-y-auto space-y-2.5 mb-6 pr-1 custom-scrollbar">
          {invalidItems.map((cartItem, idx) => {
            const item = cartItem.item || cartItem;
            const mealTimes = item.mealTimes || item.meal_times || [];
            
            const dateDisplay = mealTimes.map((mt: any) => {
              const type = (mt.mealType || mt.type || "").toUpperCase();
              const startsAt = mt.startsAt;
              const endsAt = mt.endsAt;
              const days = mt.days;

              const isLimitedTime = 
                type === 'LIMITED_TIME' || 
                (startsAt && (startsAt.includes('T') || startsAt.includes('-'))) || 
                (endsAt && (endsAt.includes('T') || endsAt.includes('-')));

              if (isLimitedTime && startsAt) {
                const startDateStr = formatDisplayDate(startsAt);
                const startTimeStr = startsAt.includes('T') ? formatDisplayTime(startsAt.split('T')[1]?.substring(0, 5)) : '';
                return startTimeStr ? `${startDateStr} at ${startTimeStr}` : startDateStr;
              }

              if (type === 'DAYS' && Array.isArray(days) && days.length > 0) {
                if (typeof days[0] === 'object' && days[0] !== null && 'day' in days[0]) {
                  return days.map((d: any) => {
                    const dayName = d.day.charAt(0).toUpperCase() + d.day.slice(1, 3);
                    const timeSlot = d.startsAt && d.endsAt ? `${formatDisplayTime(d.startsAt)} – ${formatDisplayTime(d.endsAt)}` : '';
                    return timeSlot ? `${dayName}: ${timeSlot}` : dayName;
                  }).join(' | ');
                } else {
                  const daysList = (days as string[]).map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
                  const timeWindow = startsAt && endsAt ? `${formatDisplayTime(startsAt)} – ${formatDisplayTime(endsAt)}` : '';
                  return timeWindow ? `${daysList} (${timeWindow})` : daysList;
                }
              }

              if (startsAt && endsAt) {
                return `${formatDisplayTime(startsAt)} – ${formatDisplayTime(endsAt)}`;
              }

              return 'Restricted Schedule';
            }).join(' | ') || 'Restricted Schedule';

            const primaryMealTime = mealTimes[0] || {};
            const scheduleName =
              primaryMealTime.mealTimeName ||
              primaryMealTime.name ||
              item.MealName ||
              item.category ||
              'Special Schedule';

            return (
              <div
                key={cartItem.id ?? item.pk_id ?? idx}
                className="bg-gray-50/90 hover:bg-gray-100/80 p-3.5 rounded-2xl border border-gray-200/80 flex items-center justify-between gap-3 transition-all"
              >
                <div className="flex flex-col gap-1 min-w-0">
                  <span className="font-bold text-sm text-gray-900 truncate">
                    {item.quantity ? `${item.quantity}× ` : ''}
                    {item.name}
                  </span>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-100/80 text-amber-800">
                      {scheduleName}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 bg-white border border-amber-200/60 px-2.5 py-1.5 rounded-xl text-xs font-medium text-amber-900 shrink-0 shadow-2xs">
                  <FaCalendarAlt className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span className="truncate max-w-[160px]">{dateDisplay}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onProceedWithoutInvalid}
            style={{ backgroundColor: '#FA2C37' }}
            className="flex-1 py-3.5 px-5 text-white hover:opacity-90 active:opacity-95 rounded-xl font-semibold transition-all text-xs text-center shadow-md"
          >
            Remove & Continue
          </button>
          
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3.5 px-5 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white rounded-xl font-semibold shadow-md transition-all text-xs text-center"
          >
            Change Pickup Date & Time
          </button>
        </div>
      </div>
    </div>
  );
};